/**
 * The cookie-consent bar for the v3 landing pages.
 *
 * WHY NOT `CampaignConsent.jsx`. Reusing it here is broken in a way that is
 * worse than not having a banner at all: it renders `className="cmp-consent"`,
 * whose background is `var(--cmp-navy)`, and that variable is declared on
 * `.cmp-page` rather than on `:root`. `LandingPageV3` has no `.cmp-page`
 * ancestor, so the variable resolves to nothing, the bar has no background, and
 * its `rgba(255,255,255,0.82)` text sits white-on-white. A legally load-bearing
 * element that is invisible is the worst possible outcome. It would also drag
 * in a 1,500-line stylesheet and the `/c/` variant logic, which reads and
 * WRITES `localStorage`, onto a route whose stated purpose is to not depend on
 * localStorage.
 *
 * The decision itself is shared through `useConsentChoice`, so only the markup
 * differs.
 *
 * WHY THERE IS A BANNER AT ALL. `setConsentDefaults()` denies analytics and ad
 * storage on load. With no Accept path that denial is permanent, so GA4 runs
 * cookieless for ever: no client id, no session stitching, no returning-visitor
 * or cross-page attribution, on three pages whose entire purpose is to measure
 * paid traffic. The EU requires the opt-in regardless.
 */

import React, { useLayoutEffect, useRef, useState } from 'react';

const NAVY = '#0B1E3F';
const ORANGE = '#FF6B35';
const ORANGE_HOVER = '#E8592A';
const SOFT_WHITE = '#F7F9FC';

/**
 * Presentational. The decision itself is held by `LandingPageV3` and handed
 * down, deliberately: `useConsentChoice` keeps its own `useState` per caller,
 * so a second call here would give the page a copy that never hears about the
 * click. The page needs to hear about it, because it suppresses the mobile
 * sticky CTA until this bar is answered.
 *
 * ONE BAR AT A TIME. Adi, 3 November 2026: on a phone the cookie text hid the
 * page. Two bars were fixed to the same edge, this one lifted above the sticky
 * CTA by a measured offset, and together with wrapped text they took most of a
 * short viewport. The offset is gone because the overlap it solved can no
 * longer happen.
 *
 * It also reserves its own height in normal flow. It had no spacer before, so
 * at the bottom of the page it sat over whatever was there, which on a phone
 * is the submit button of the form it is asking you to fill in.
 *
 * @param {boolean}  visible  whether a choice is still outstanding
 * @param {Function} accept   grant analytics storage
 * @param {Function} decline  deny it
 */
export default function LandingConsent({ visible, accept, decline }) {
  const barRef = useRef(null);
  const [barH, setBarH] = useState(0);

  /*
   * Measured, not assumed. The strip is one line on a wide screen and two or
   * three on a narrow one, so a hardcoded spacer is wrong at most widths. The
   * same guarded-ResizeObserver shape as the sticky bar in LandingPageV3.
   */
  useLayoutEffect(() => {
    if (!visible) { setBarH(0); return undefined; }
    const el = barRef.current;
    if (!el) return undefined;
    const measure = () => {
      const h = el.getBoundingClientRect().height;
      if (h > 0) setBarH(h);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [visible]);

  if (!visible) return null;

  const button = (primary) => ({
    minHeight: 36,
    padding: '0 14px',
    borderRadius: 6,
    border: primary ? 0 : '1px solid rgba(247,249,252,0.35)',
    background: primary ? ORANGE : 'transparent',
    color: SOFT_WHITE,
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  });

  return (
    <>
      {/*
        * Reserves the strip's height at the end of the document so the bar
        * cannot sit over the bottom of the page. Nothing did this before.
        */}
      <div aria-hidden="true" style={{ height: barH }} />
      <div
        ref={barRef}
        role="region"
        aria-label="Cookie choices"
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 60,
          display: 'flex',
          /*
           * `nowrap` is what makes this a strip. Wrapping sent the buttons
           * onto their own row on a phone, which roughly doubled the height
           * of a bar that is already fixed over the page.
           */
          flexWrap: 'nowrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          /*
           * `max()`, not `calc()`. With the default `viewport-fit=auto` iOS
           * lays the page out inside the safe area, so this inset is 0 and
           * correctly so. See the matching note on the sticky bar in
           * LandingPageV3.jsx.
           *
           * The vertical padding scales with the viewport so the strip is
           * comfortable on a desktop and tight on a phone, with no breakpoint
           * and no resize listener.
           */
          padding: 'clamp(8px, 1.6vw, 14px) clamp(12px, 2.4vw, 20px) max(clamp(8px, 1.6vw, 14px), env(safe-area-inset-bottom))',
          background: NAVY,
          boxShadow: '0 -4px 16px rgba(11,30,63,0.18)',
        }}
      >
        <p style={{
          margin: 0,
          // Shrinks on a narrow screen so the sentence keeps to two or three
          // short lines instead of five.
          fontSize: 'clamp(12px, 3vw, 14px)',
          lineHeight: 1.4,
          color: SOFT_WHITE,
          maxWidth: 720,
          // Lets the text give way to the buttons rather than pushing them out.
          minWidth: 0,
        }}
        >
          We use cookies to measure how this page performs. Decline and nothing
          is stored on your device.{' '}
          <a
            href="https://alchemiq.ai/privacy"
            target="_blank"
            rel="noreferrer"
            style={{ color: SOFT_WHITE, fontWeight: 600 }}
          >
            Privacy Policy
          </a>.
        </p>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {/* Equal weight, no dark pattern: declining is one click, same as accepting. */}
          <button type="button" onClick={decline} style={button(false)}>Decline</button>
          <button
            type="button"
            onClick={accept}
            style={button(true)}
            onMouseOver={(e) => { e.currentTarget.style.background = ORANGE_HOVER; }}
            onMouseOut={(e) => { e.currentTarget.style.background = ORANGE; }}
          >
            Accept
          </button>
        </div>
      </div>
    </>
  );
}
